import { useCallback, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import type { ReceiptsMutationFormTyping } from "@components/Dashboard/pages/PurchasingPage/Receive/components/MutationLayout";

import type { ReceiptDocumentItem } from "./FileRow";
import Panel from "./Panel";

export default function CreationSection() {
    const { control, setValue } = useFormContext<ReceiptsMutationFormTyping>();

    const files =
        useWatch({
            control,
            name: "attachments",
        }) ?? [];

    const documentItems = useMemo<ReceiptDocumentItem[]>(
        () =>
            files.map((file, index) => ({
                type: "local",
                key: `${file.name}-${file.size}-${file.lastModified}-${index}`,
                fileName: file.name,
                size: file.size,
                mimeType: file.type,
                file,
                index,
            })),
        [files]
    );

    const handleFilesAccepted = useCallback(
        (incomingFiles: File[]) => {
            setValue("attachments", [...files, ...incomingFiles], {
                shouldDirty: true,
                shouldValidate: true,
            });
        },
        [files, setValue]
    );

    const handleDelete = useCallback(
        (item: ReceiptDocumentItem) => {
            if (item.type !== "local") return;

            setValue(
                "attachments",
                files.filter((_, index) => index !== item.index),
                {
                    shouldDirty: true,
                    shouldValidate: true,
                }
            );
        },
        [files, setValue]
    );

    return (
        <Controller
            name="document_number"
            control={control}
            render={({ field, fieldState }) => (
                <Panel
                    documentNumberValue={field.value}
                    documentNumberError={fieldState.error}
                    files={documentItems}
                    onDocumentNumberChange={field.onChange}
                    onFilesAccepted={handleFilesAccepted}
                    onDownload={(item) => {
                        if (item.type === "local") {
                            downloadLocalFile(item.file);
                        }
                    }}
                    onDelete={handleDelete}
                />
            )}
        />
    );
}

function downloadLocalFile(file: File) {
    const url = window.URL.createObjectURL(file);
    triggerBrowserDownload(url, file.name);
    window.URL.revokeObjectURL(url);
}

function triggerBrowserDownload(url: string, fileName: string) {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;

    document.body.appendChild(link);
    link.click();
    link.remove();
}
